
import { useEffect } from 'react';

interface SeoHeadProps {
  title: string;
  description: string;
  keywords?: string;
  canonical: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  jsonLd?: object | object[];
}

export default function SeoHead({
  title,
  description,
  keywords,
  canonical,
  ogTitle,
  ogDescription,
  ogImage,
  jsonLd,
}: SeoHeadProps) {
  const siteUrl = (import.meta.env.VITE_SITE_URL as string | undefined) ?? 'https://example.com';
  const fullCanonical = `${siteUrl}${canonical}`;
  const resolvedOgImage =
    ogImage ?? 'https://public.readdy.ai/ai/img_res/56c79e41-d967-4e72-aa45-446ef434d0a7.png';

  useEffect(() => {
    document.title = title;

    const setMeta = (selector: string, value: string) => {
      let el = document.querySelector<HTMLMetaElement>(selector);
      if (!el) {
        el = document.createElement('meta');
        const inner = selector.replace(/^\[|\]$/g, '');
        const eqIdx = inner.indexOf('=');
        const attrName = inner.slice(0, eqIdx);
        const attrVal = inner.slice(eqIdx + 1).replace(/^"|"$/g, '');
        el.setAttribute(attrName, attrVal);
        document.head.appendChild(el);
      }
      el.setAttribute('content', value);
    };

    setMeta('meta[name="description"]', description);
    if (keywords) setMeta('meta[name="keywords"]', keywords);

    let canonicalEl = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonicalEl) {
      canonicalEl = document.createElement('link');
      canonicalEl.rel = 'canonical';
      document.head.appendChild(canonicalEl);
    }
    canonicalEl.href = fullCanonical;

    setMeta('meta[property="og:title"]', ogTitle ?? title);
    setMeta('meta[property="og:description"]', ogDescription ?? description);
    setMeta('meta[property="og:url"]', fullCanonical);
    setMeta('meta[property="og:image"]', resolvedOgImage);
    setMeta('meta[name="twitter:title"]', ogTitle ?? title);
    setMeta('meta[name="twitter:description"]', ogDescription ?? description);
    setMeta('meta[name="twitter:image"]', resolvedOgImage);

    document.querySelectorAll('script[data-seo-jsonld]').forEach((s) => s.remove());

    const schemas = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];
    schemas.forEach((schema) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-seo-jsonld', 'true');
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });

    return () => {
      document.querySelectorAll('script[data-seo-jsonld]').forEach((s) => s.remove());
    };
  }, [title, description, keywords, fullCanonical, ogTitle, ogDescription, resolvedOgImage, jsonLd]);

  return null;
}